// Dashboard view for the Org tab.
//
// Ports worker2.js's five surfaces into hlpt-copilot:
//   - Top 10 utilized individuals (Chart.js bar)
//   - Bottom 10 utilized individuals (Chart.js bar)
//   - Top Tasks horizontal bar chart (top 2 per department + legend)
//   - Task Allocation Breakdown accordion (dept → task → sub-task)
//   - Nested team breakdown: manager → subteam → individual contributors
//
// Individuals = nodes without their own team (hasTeam === false). The
// top/bottom utilization charts include anyone with non-zero Hubstaff
// OR Tracked hours (worker2.js fallback — without this, employees
// whose Hubstaff data is sparse but who logged time in Co-Pilot would
// silently disappear from the chart).

import { useEffect, useMemo, useRef, useState } from 'react';
import { Chart } from 'chart.js/auto';

function getBadgeClass(util) {
  if (util >= 80) return 'util-good';
  if (util >= 50) return 'util-warn';
  return 'util-poor';
}

// Department palette for the Top Tasks chart + Allocation legend. Hue
// per dept so a glance at the chart immediately groups bars; same key
// drives both the bar fill and the dot in the legend / accordion header.
const DEPT_COLORS = {
  'Guest Support':              '#3B82F6',  // blue
  'Agency Support':             '#10B981',  // green
  'Branded Onboarding':         '#F59E0B',  // amber
  'Products':                   '#8B5CF6',  // violet
  'Operations - Projects':      '#EF4444',  // red
  'Operations - Admins':        '#EC4899',  // pink
  'Guest Experience and Billing': '#06B6D4', // cyan
  'Other':                      '#6B7280',  // gray (catch-all)
};
function colorForDept(dept) {
  return DEPT_COLORS[dept] || DEPT_COLORS.Other;
}

// Aggregate every node's taskBreakdown across the visible set into a
// single nested map: { [dept]: { [task]: { [sub]: totalSeconds } } }.
// Returns null when no node has a non-empty breakdown — caller hides
// the surfaces in that case.
function aggregateTaskData(nodes) {
  const out = {};
  let any = false;
  for (const n of (nodes || [])) {
    const tb = n.taskBreakdown;
    if (!tb || typeof tb !== 'object') continue;
    for (const [dept, tasks] of Object.entries(tb)) {
      if (!tasks || typeof tasks !== 'object') continue;
      for (const [task, subs] of Object.entries(tasks)) {
        if (!subs || typeof subs !== 'object') continue;
        for (const [sub, sec] of Object.entries(subs)) {
          const s = Number(sec) || 0;
          if (!s) continue;
          any = true;
          out[dept] ||= {};
          out[dept][task] ||= {};
          out[dept][task][sub] = (out[dept][task][sub] || 0) + s;
        }
      }
    }
  }
  return any ? out : null;
}

function formatHoursShort(seconds) {
  const h = (seconds || 0) / 3600;
  if (h < 0.1) return `${Math.round(seconds / 60)}m`;
  return `${h.toFixed(h < 10 ? 2 : 1)}h`;
}

function BarChart({ id, labels, values, color }) {
  const ref = useRef(null);
  const chartRef = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    if (chartRef.current) chartRef.current.destroy();
    chartRef.current = new Chart(ref.current.getContext('2d'), {
      type: 'bar',
      data: {
        labels,
        datasets: [{ label: 'Utilization %', data: values, backgroundColor: color, borderRadius: 4 }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          y: { beginAtZero: true, title: { display: true, text: 'Utilization %' } },
          x: { ticks: { autoSkip: false, maxRotation: 45, minRotation: 0 } },
        },
      },
    });
    return () => { chartRef.current?.destroy(); chartRef.current = null; };
  }, [labels.join('|'), values.join('|'), color]);
  return <canvas ref={ref} id={id} />;
}

// Build manager → [children] index for nested rendering.
function childrenIndex(nodes) {
  const map = new Map();
  for (const n of nodes) {
    if (!n.parentId) continue;
    if (!map.has(n.parentId)) map.set(n.parentId, []);
    map.get(n.parentId).push(n);
  }
  // Stable sort within each bucket: highest utilization first.
  for (const arr of map.values()) {
    arr.sort((a, b) => (b.utilization || 0) - (a.utilization || 0));
  }
  return map;
}

function countDescendants(nodes, parentId, idx, seen = new Set()) {
  if (seen.has(parentId)) return 0;
  seen.add(parentId);
  let count = 0;
  for (const c of (idx.get(parentId) || [])) {
    count += 1;
    count += countDescendants(nodes, c.id, idx, seen);
  }
  return count;
}

// Horizontal bar chart: top 2 tasks per department across the visible
// set, sorted by total hours. Ports worker2.js's "Top Tasks" surface.
// Each bar is colored by department so the chart groups visually
// without the user reading labels. Renders alongside a sidebar legend
// (rendered in the parent layout, not here) for the same reason.
function TopTasksChart({ id, aggregate }) {
  const ref = useRef(null);
  const chartRef = useRef(null);

  const items = useMemo(() => {
    if (!aggregate) return [];
    const rows = [];
    for (const [dept, tasks] of Object.entries(aggregate)) {
      const byTask = Object.entries(tasks).map(([task, subs]) => {
        const totalSec = Object.values(subs).reduce((a, b) => a + (Number(b) || 0), 0);
        return { dept, task, totalSec };
      }).sort((a, b) => b.totalSec - a.totalSec).slice(0, 2);
      rows.push(...byTask);
    }
    // Final sort: hottest tasks across all depts go to the TOP of the
    // chart (horizontal bars render reversed — last row = top of axis).
    rows.sort((a, b) => a.totalSec - b.totalSec);
    return rows;
  }, [aggregate]);

  useEffect(() => {
    if (!ref.current) return;
    if (chartRef.current) chartRef.current.destroy();
    if (!items.length) return;
    chartRef.current = new Chart(ref.current.getContext('2d'), {
      type: 'bar',
      data: {
        labels: items.map(it => it.task),
        datasets: [{
          label: 'Hours',
          data: items.map(it => Number((it.totalSec / 3600).toFixed(2))),
          backgroundColor: items.map(it => colorForDept(it.dept)),
          borderRadius: 4,
        }],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: ctx => {
                const it = items[ctx.dataIndex];
                return `${it.dept}: ${ctx.parsed.x}h`;
              },
            },
          },
        },
        scales: {
          x: { beginAtZero: true, title: { display: true, text: 'Hours' } },
          y: { ticks: { autoSkip: false } },
        },
      },
    });
    return () => { chartRef.current?.destroy(); chartRef.current = null; };
  }, [items]);

  if (!items.length) {
    return <div className="h-full flex items-center justify-center text-muted text-sm">No task data in this range.</div>;
  }
  return <canvas ref={ref} id={id} />;
}

// Sidebar legend that pairs with TopTasksChart. Lists departments
// represented in the current aggregate with a colored dot + total
// hours rolled up across that dept.
function TopTasksLegend({ aggregate }) {
  const rows = useMemo(() => {
    if (!aggregate) return [];
    const out = [];
    for (const [dept, tasks] of Object.entries(aggregate)) {
      let sec = 0;
      for (const subs of Object.values(tasks)) {
        for (const s of Object.values(subs)) sec += Number(s) || 0;
      }
      out.push({ dept, sec });
    }
    out.sort((a, b) => b.sec - a.sec);
    return out;
  }, [aggregate]);
  if (!rows.length) return null;
  return (
    <ul className="space-y-2 text-sm">
      {rows.map(r => (
        <li key={r.dept} className="flex items-center gap-2.5">
          <span
            className="w-2.5 h-2.5 rounded-full shrink-0"
            style={{ background: colorForDept(r.dept) }}
            aria-hidden
          />
          <span className="flex-1 min-w-0 truncate">{r.dept}</span>
          <span className="text-xs text-muted font-mono">{(r.sec / 3600).toFixed(1)}h</span>
        </li>
      ))}
    </ul>
  );
}

// Three-level accordion: department → task → sub-task. Mirrors worker2's
// "Task Allocation Breakdown". For tasks with a single sub-task we
// collapse the inner level into a flat row to reduce visual noise.
// At most ONE department is open at a time at the top level — clicking
// another department closes the previous one (auto-close behavior from
// worker2.js).
function TaskAllocationBreakdown({ aggregate }) {
  const rootRef = useRef(null);

  // Auto-collapse other top-level <details> when one opens — ports
  // worker2.js's accordion-only-one-open behavior. We attach the
  // listener via ref to avoid re-binding on every render.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const onToggle = (e) => {
      const opened = e.target;
      if (!opened || opened.tagName !== 'DETAILS') return;
      if (!opened.classList.contains('alloc-top-level')) return;
      if (!opened.open) return;
      for (const d of root.querySelectorAll('details.alloc-top-level')) {
        if (d !== opened && d.open) d.open = false;
      }
    };
    // toggle bubbles in modern browsers; attach at root.
    root.addEventListener('toggle', onToggle, true);
    return () => root.removeEventListener('toggle', onToggle, true);
  }, []);

  const rows = useMemo(() => {
    if (!aggregate) return [];
    const out = [];
    for (const [dept, tasks] of Object.entries(aggregate)) {
      const taskRows = [];
      let deptSec = 0;
      for (const [task, subs] of Object.entries(tasks)) {
        const subRows = Object.entries(subs)
          .map(([sub, sec]) => ({ sub, sec: Number(sec) || 0 }))
          .sort((a, b) => b.sec - a.sec);
        const taskSec = subRows.reduce((a, b) => a + b.sec, 0);
        deptSec += taskSec;
        taskRows.push({ task, taskSec, subRows });
      }
      taskRows.sort((a, b) => b.taskSec - a.taskSec);
      out.push({ dept, deptSec, taskRows });
    }
    out.sort((a, b) => b.deptSec - a.deptSec);
    return out;
  }, [aggregate]);

  if (!rows.length) {
    return <div className="p-5 rounded-md border border-border bg-panel text-center text-sm text-muted">No task data in this range.</div>;
  }

  return (
    <div ref={rootRef} className="space-y-2">
      {rows.map(d => (
        <details key={d.dept} className="alloc-top-level rounded-md border border-border bg-panel overflow-hidden">
          <summary className="px-4 py-3 cursor-pointer bg-panel2/40 hover:bg-panel2 flex items-center justify-between gap-3 list-none [&::-webkit-details-marker]:hidden">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ background: colorForDept(d.dept) }}
                aria-hidden
              />
              <span className="font-semibold text-text truncate">{d.dept}</span>
              <span className="text-xs text-muted">({d.taskRows.length} task{d.taskRows.length === 1 ? '' : 's'})</span>
            </div>
            <span className="util-badge util-neutral shrink-0">{(d.deptSec / 3600).toFixed(1)}h</span>
          </summary>
          <div className="border-t border-border divide-y divide-border">
            {d.taskRows.map(t => {
              // Single-sub-task tasks render flat — no nested accordion.
              if (t.subRows.length === 1) {
                return (
                  <div key={t.task} className="flex items-center gap-3 px-4 py-2.5">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{t.task}</div>
                      <div className="text-xs text-muted truncate">{t.subRows[0].sub}</div>
                    </div>
                    <span className="text-xs text-text/80 font-mono shrink-0">{formatHoursShort(t.taskSec)}</span>
                  </div>
                );
              }
              return (
                <details key={t.task}>
                  <summary className="px-4 py-2.5 cursor-pointer hover:bg-panel2/30 flex items-center justify-between gap-3 list-none [&::-webkit-details-marker]:hidden">
                    <div className="flex items-center gap-2 min-w-0">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-muted shrink-0">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                      <span className="text-sm font-medium truncate">{t.task}</span>
                      <span className="text-[11px] text-muted">({t.subRows.length} sub-tasks)</span>
                    </div>
                    <span className="text-xs text-text/80 font-mono shrink-0">{formatHoursShort(t.taskSec)}</span>
                  </summary>
                  <div className="bg-panel2/30 divide-y divide-border/50">
                    {t.subRows.map(s => (
                      <div key={s.sub} className="flex items-center gap-3 px-8 py-2">
                        <div className="flex-1 min-w-0 text-xs text-text/80 truncate">{s.sub}</div>
                        <span className="text-xs text-muted font-mono shrink-0">{formatHoursShort(s.sec)}</span>
                      </div>
                    ))}
                  </div>
                </details>
              );
            })}
          </div>
        </details>
      ))}
    </div>
  );
}

function ContributorTable({ rows }) {
  return (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="bg-panel border-b border-border">
          <th className="text-left text-[11px] uppercase tracking-wider text-muted font-semibold px-4 py-2.5">Contributor</th>
          <th className="text-left text-[11px] uppercase tracking-wider text-muted font-semibold px-4 py-2.5">Position</th>
          <th className="text-left text-[11px] uppercase tracking-wider text-muted font-semibold px-4 py-2.5">Tracked</th>
          <th className="text-left text-[11px] uppercase tracking-wider text-muted font-semibold px-4 py-2.5">Hubstaff</th>
          <th className="text-left text-[11px] uppercase tracking-wider text-muted font-semibold px-4 py-2.5">Self Util %</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.id} className="border-b border-border last:border-b-0 hover:bg-panel2/50">
            <td className="px-4 py-3 font-medium text-text">{r.name}</td>
            <td className="px-4 py-3 text-muted text-xs">{r.position}</td>
            <td className="px-4 py-3 text-text/80">{r.trackedHours}h</td>
            <td className="px-4 py-3 text-text/80">{r.hubstaffHours}h</td>
            <td className="px-4 py-3"><span className={`util-badge ${getBadgeClass(r.utilization)}`}>{r.utilization}%</span></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ManagerAccordion({ manager, idx, descCount }) {
  const children = idx.get(manager.id) || [];
  const contributors = children.filter(c => !c.hasTeam);
  const subManagers = children.filter(c => c.hasTeam);
  return (
    <details className="rounded-md border border-border bg-panel overflow-hidden">
      <summary className="px-4 py-3 cursor-pointer bg-panel2/40 hover:bg-panel2 flex items-center justify-between gap-3 list-none [&::-webkit-details-marker]:hidden">
        <div className="flex items-center gap-2 min-w-0">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted shrink-0 transition-transform group-open:rotate-180">
            <polyline points="6 9 12 15 18 9" />
          </svg>
          <span className="font-semibold text-text truncate">{manager.name}</span>
          <span className="text-xs text-muted">({descCount} {descCount === 1 ? 'member' : 'members'})</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`util-badge ${getBadgeClass(manager.utilization)}`}>Self: {manager.utilization}%</span>
          <span className={`util-badge ${getBadgeClass(manager.teamUtilization)}`}>Team: {manager.teamUtilization}%</span>
        </div>
      </summary>
      <div className="border-t border-border">
        {contributors.length > 0 && <ContributorTable rows={contributors} />}
        {subManagers.length > 0 && (
          <div className="p-3 space-y-2 bg-panel2/20">
            {subManagers.map(sm => (
              <ManagerAccordion
                key={sm.id}
                manager={sm}
                idx={idx}
                descCount={countDescendants(null, sm.id, idx)}
              />
            ))}
          </div>
        )}
        {contributors.length === 0 && subManagers.length === 0 && (
          <div className="p-4 text-center text-xs text-muted">No active reports in range.</div>
        )}
      </div>
    </details>
  );
}

export default function OrgDashboard({ data }) {
  const nodes = data?.nodes || [];

  // Top/bottom 10 individuals — anyone with non-zero Hubstaff OR
  // Tracked hours qualifies (worker2.js fallback). Without the OR,
  // people whose Hubstaff sync is sparse but who actively logged time
  // in Co-Pilot would silently disappear from the chart.
  const validIndividuals = useMemo(() => (
    nodes.filter(n => !n.hasTeam && (n.hubstaffHours > 0 || n.trackedHours > 0))
      .slice() // copy before sort
      .sort((a, b) => (b.utilization || 0) - (a.utilization || 0))
  ), [nodes]);

  const top10 = validIndividuals.slice(0, 10);
  const bot10 = [...validIndividuals].reverse().slice(0, 10);

  // Task aggregate drives both the Top Tasks chart and the Allocation
  // Breakdown — compute once, share. Null when nothing's logged in the
  // window (the surfaces are hidden in that case).
  const taskAggregate = useMemo(() => aggregateTaskData(nodes), [nodes]);

  // Top-level managers = nodes whose parentId is empty OR whose parent
  // is the synthetic root. For a leaf-rooted view, the viewer themself
  // is the only top-level entry (and renders as their own accordion).
  const idx = useMemo(() => childrenIndex(nodes), [nodes]);
  const topLevel = useMemo(() => (
    nodes
      .filter(n => !n.parentId)
      .filter(n => n.hasTeam || n.id === data?.viewer?.hrId)
      .sort((a, b) => (b.teamUtilization || 0) - (a.teamUtilization || 0))
  ), [nodes, data?.viewer?.hrId]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-panel p-5">
          <div className="text-sm font-semibold mb-3">Top 10 utilized individuals</div>
          <div className="h-72">
            {top10.length > 0
              ? <BarChart id="chartTop" labels={top10.map(d => d.name)} values={top10.map(d => d.utilization)} color="#10B981" />
              : <div className="h-full flex items-center justify-center text-muted text-sm">No data for this range.</div>}
          </div>
        </div>
        <div className="rounded-lg border border-border bg-panel p-5">
          <div className="text-sm font-semibold mb-3">Lowest 10 utilized individuals</div>
          <div className="h-72">
            {bot10.length > 0
              ? <BarChart id="chartBot" labels={bot10.map(d => d.name)} values={bot10.map(d => d.utilization)} color="#EF4444" />
              : <div className="h-full flex items-center justify-center text-muted text-sm">No data for this range.</div>}
          </div>
        </div>
      </div>

      {/* Top Tasks chart — only render the card when there's data to
          show. Layout: chart on the left, legend sidebar on the right
          (or stacked on narrow screens). */}
      {taskAggregate && (
        <div className="rounded-lg border border-border bg-panel p-5">
          <div className="text-sm font-semibold mb-3">Top tasks (last range)</div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-4">
            <div className="h-72 md:h-96">
              <TopTasksChart id="chartTopTasks" aggregate={taskAggregate} />
            </div>
            <div className="md:border-l md:border-border md:pl-4 max-h-96 overflow-y-auto">
              <div className="text-[11px] uppercase tracking-wider text-muted font-semibold mb-2">
                Departments
              </div>
              <TopTasksLegend aggregate={taskAggregate} />
            </div>
          </div>
        </div>
      )}

      {/* Task Allocation Breakdown — nested accordions: dept → task →
          sub-task. Auto-collapses other top-level departments so the
          page doesn't accordion-explode. */}
      {taskAggregate && (
        <div className="space-y-3">
          <div className="text-sm font-semibold">Task allocation breakdown</div>
          <TaskAllocationBreakdown aggregate={taskAggregate} />
        </div>
      )}

      <div className="space-y-3">
        <div className="text-sm font-semibold">Interactive team breakdown</div>
        {topLevel.length > 0 ? (
          topLevel.map(mgr => (
            <ManagerAccordion
              key={mgr.id}
              manager={mgr}
              idx={idx}
              descCount={countDescendants(nodes, mgr.id, idx)}
            />
          ))
        ) : (
          <div className="p-5 rounded-md border border-border bg-panel text-center text-sm text-muted">
            No team hierarchy in this scope.
          </div>
        )}
      </div>
    </div>
  );
}
